import { useEffect, useRef } from 'react';

/**
 * Reports a saved view's filters upward whenever the active view changes.
 *
 * A tab is a filter the user set, so it is reported exactly as a dropdown filter
 * is. Without this a page whose read can be narrowed by the server would narrow
 * only the rows already on screen, and the tab would be a claim about the page
 * rather than about the collection.
 *
 * The report is an EFFECT, not part of the render-time page reset: the caller
 * answers it with its own `setState`, and calling that while this component is
 * rendering is exactly what React rejects with "Cannot update a component while
 * rendering a different component".
 *
 * The first render is not a change — the view the table opened on is the view the
 * caller already knows about — so it is recorded and not reported.
 */
export function useViewFilterReport(
  viewFilters: Record<string, string>,
  onReport: ((filters: Record<string, string>) => void) | undefined,
): void {
  const key = JSON.stringify(viewFilters);
  const reported = useRef(key);

  useEffect(() => {
    if (reported.current === key) return;
    reported.current = key;
    onReport?.(viewFilters);
  }, [key, viewFilters, onReport]);
}
